import { patchState } from '@ngrx/signals';
import { LocalStorageStore } from '../models/local-storage.models';
import { LOCAL_STORAGE_INITIAL_STATE } from '../constants/local-storage.constants';

/**
 * Retrieves a value from localStorage, parses it if it's JSON, and updates the store.
 * @param store - The LocalStorageStore.
 * @param key - The key to retrieve.
 */
export const getItem = <T>(store: LocalStorageStore, key: string): T | null => {
  const value = localStorage.getItem(key);
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return value as unknown as T;
  }
};

/**
 * Sets a value in localStorage, stringifies it if it's an object, and updates the store.
 * @param store - The LocalStorageStore.
 * @param key - The key to set.
 * @param value - The value to store.
 */
export const setItem = (store: LocalStorageStore, key: string, value: any) => {
  const serializedValue = typeof value === 'object' ? JSON.stringify(value) : value;
  localStorage.setItem(key, serializedValue);
  patchState(store, { [key]: value });
};

/**
 * Removes an item from localStorage and updates the store.
 * @param store - The LocalStorageStore.
 * @param key - The key to remove.
 */
export const removeItem = (store: LocalStorageStore, key: string) => {
  localStorage.removeItem(key);
  patchState(store, { [key]: null });
};

/**
 * Clears all localStorage data and resets the store state.
 * @param store - The LocalStorageStore.
 */
export const clearStorage = (store: LocalStorageStore) => {
  localStorage.clear();
  patchState(store, LOCAL_STORAGE_INITIAL_STATE);
};
